import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { createAuthorizedConsumer } from "./transport";
import { collaborationSessionId } from "./session";

export { collaborationCableUrl } from "./transport";

/** Opaque edit/lock versions: integers for calculations, ISO8601 for reports. */
export function collaborationVersionGreater(candidate, baseline) {
  if (candidate == null || baseline == null) return false;
  const a = String(candidate);
  const b = String(baseline);
  if (a === b) return false;
  if (/^-?\d+$/.test(a) && /^-?\d+$/.test(b)) return Number(a) > Number(b);
  return a > b;
}

export function useRecordCollaboration({ report, record = report, recordType = "report", section = "overview", editing = false, enabled = true, sendPresence = true }) {
  const companyId = useSelector((state) => state.session.currentCompany?.id);
  const userId = useSelector((state) => state.session.currentUser?.id);
  const [state, setState] = useState({ connected: false, connecting: true, users: [], changed: false });
  const [attempt, setAttempt] = useState(0);
  const latest = useRef({ record, section, editing, sendPresence });
  latest.current = { record, section, editing, sendPresence };
  const heartbeatRef = useRef(null);
  const revisionRef = useRef(0);

  useEffect(() => {
    if (!enabled || !record?.id || !companyId) return;
    let active = true, consumer, subscription, heartbeatTimer, reconnectTimer;
    let reconnectAttempt = 0;
    let busy = false, recovering = false, denied = false;
    let ownCommittedVersion;
    let notifiedVersion;
    const changed = (version, section) => {
      const next = version == null ? undefined : String(version);
      if (next != null && next === notifiedVersion) return;
      // Never rewind notifiedVersion — a late stale snapshot must not replace
      // a newer remote notice (that sticks the banner after the editor catches up).
      if (next != null && notifiedVersion != null && !collaborationVersionGreater(next, notifiedVersion)) return;
      if (next != null) notifiedVersion = next;
      const revision = ++revisionRef.current;
      setState(prev => ({ ...prev, changed: true, revision, section }));
    };
    const receivedEvents = new Set();
    setState({ connected: false, connecting: true, users: [], changed: false });
    const heartbeat = () => {
      // Keep the Cable subscription for invalidation, but do not publish presence
      // when an overlay owns the editor — otherwise overview "viewing" overwrites it.
      if (!active || denied || !latest.current.sendPresence) return;
      subscription?.perform("heartbeat", {
        section: latest.current.section,
        state: document.hidden ? "away" : latest.current.editing ? "editing" : "viewing",
      });
    };
    heartbeatRef.current = heartbeat;
    const connect = async () => {
      if (!active || busy || denied) return;
      busy = true;
      recovering = false;
      try {
        consumer = await createAuthorizedConsumer({ record_type: recordType, record_id: record.id });
        if (!active) { consumer.disconnect(); return; }
        subscription = consumer.subscriptions.create({ channel: "RecordCollaborationChannel" }, {
          connected() {
            if (!active) return;
            reconnectAttempt = 0;
            setState((prev) => ({ ...prev, connected: true, connecting: false }));
            // Reconnect reviews via heartbeat snapshot: matching versions are not
            // "new changes"; only real remote drift notifies.
            heartbeat();
          },
          disconnected() { recover(); },
          rejected() { recover(); },
          received(message) {
            if (!active) return;
            if (message.type === "access_revoked") {
              denied = true;
              consumer?.disconnect();
              window.clearTimeout(reconnectTimer);
              setState({ connected: false, connecting: false, users: [], changed: true, denied: true });
              return;
            }
            if (message.type === "presence_unavailable") {
              setState((prev) => ({ ...prev, connected: false, connecting: false, users: [] }));
            } else if (message.type === "presence") {
              setState((prev) => ({ ...prev, connected: true, connecting: false, users: (message.users || []).filter((row) => String(row.user_id) !== String(userId)) }));
            } else if (message.type === "snapshot") {
              const current = latest.current.record;
              const version = recordType === "calculation" ? current?.lock_version : current?.edit_version;
              const remote = message.edit_version == null ? null : String(message.edit_version);
              const local = version == null ? null : String(version);
              const own = ownCommittedVersion == null ? null : String(ownCommittedVersion);
              if (remote == null) {
                // ignore incomplete snapshots
              } else if (remote === local) {
                // Editor matches server. Clear unless we still know about a newer
                // notified remote version than this snapshot.
                if (notifiedVersion == null || !collaborationVersionGreater(notifiedVersion, remote)) {
                  notifiedVersion = remote;
                  setState((prev) => (prev.changed ? { ...prev, changed: false } : prev));
                }
              } else if (remote === own) {
                // Own after_commit echo before HTTP updates the editor — not drift.
              } else if (local != null && !collaborationVersionGreater(remote, local)) {
                // Snapshot at or behind the editor — ignore (late heartbeat/own echo).
              } else if (notifiedVersion != null && !collaborationVersionGreater(remote, notifiedVersion)) {
                // Already notified about this or a newer version — do not rewind.
              } else {
                changed(message.edit_version);
              }
            } else if (message.type === "changed" && message.session_id === collaborationSessionId() && message.section !== "report_type") {
              // Type settings return the type, not a refreshed report snapshot.
              // Their invalidation must also reach reports in this same session.
              // after_commit can arrive before our HTTP save response updates
              // the editor. A heartbeat in that window is not a remote edit.
              ownCommittedVersion = String(message.edit_version);
            } else if (message.type === "changed" && !receivedEvents.has(message.event_id)) {
              receivedEvents.add(message.event_id);
              if (receivedEvents.size > 100) receivedEvents.delete(receivedEvents.values().next().value);
              changed(message.edit_version, message.section);
            }
          },
        });
      } catch (error) {
        if (!active) return;
        if ([401, 403, 404].includes(error?.response?.status)) {
          denied = true;
          setState({ connected: false, connecting: false, users: [], changed: false, denied: true });
        } else recover();
      } finally { busy = false; }
    };
    // Tickets expire quickly. Reconnect obtains a fresh ticket rather than reusing
    // an OAuth bearer in a URL. One backoff timer owns recovery.
    const recover = () => {
      if (!active || recovering || denied) return;
      recovering = true;
      setState((prev) => ({ ...prev, connected: false, connecting: false, users: [] }));
      consumer?.disconnect();
      window.clearTimeout(reconnectTimer);
      reconnectTimer = window.setTimeout(connect, Math.min(30000, 1000 * (2 ** Math.min(reconnectAttempt++, 5))));
    };
    connect();
    heartbeatTimer = window.setInterval(heartbeat, 25000);
    window.addEventListener("focus", heartbeat);
    document.addEventListener("visibilitychange", heartbeat);
    return () => {
      active = false;
      heartbeatRef.current = null;
      window.clearInterval(heartbeatTimer);
      window.clearTimeout(reconnectTimer);
      window.removeEventListener("focus", heartbeat);
      document.removeEventListener("visibilitychange", heartbeat);
      subscription?.unsubscribe();
      consumer?.disconnect();
    };
  }, [record?.id, recordType, companyId, userId, enabled, attempt]);

  useEffect(() => { heartbeatRef.current?.(); }, [section, editing, sendPresence]);
  return { ...state, scope: `${companyId}:${userId}:${recordType}:${record?.id}`, retry: () => setAttempt((n) => n + 1),
    acknowledge: revision => setState(prev => prev.revision === revision ? { ...prev, changed: false } : prev),
  };
}
